import React, { useState } from "react";
import useSWR from "swr";
import api from "@/lib/api";
import { formatIDR, formatDate, todayISO } from "@/lib/format";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { FileSpreadsheet, FileText, TrendingUp, TrendingDown, Wallet } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, LineChart, Line } from "recharts";

const fetcher = (url) => api.get(url).then((r) => r.data);

function firstDayOfMonth() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}

const CAT_LABEL = {
  renovasi: "Renovasi", pajak: "Pajak", komisi: "Komisi",
  utilitas: "Utilitas", insidental: "Insidental", lainnya: "Lainnya",
};

export default function Reports() {
  const [range, setRange] = useState({ from: firstDayOfMonth(), to: todayISO() });
  const qs = new URLSearchParams({ period_from: range.from, period_to: range.to });
  const { data, isLoading } = useSWR(`/reports/summary?${qs.toString()}`, fetcher);
  const { data: trend } = useSWR(`/reports/trend?months=6`, fetcher);

  const MONTH_ID = ["Jan","Feb","Mar","Apr","Mei","Jun","Jul","Ags","Sep","Okt","Nov","Des"];
  const trendData = (trend?.months || []).map((m) => {
    const [y, mm] = m.month.split("-");
    return { label: `${MONTH_ID[parseInt(mm,10)-1]} ${y.slice(2)}`, Pemasukan: m.income, Pengeluaran: m.expense, Net: m.net };
  });

  const chartData = data
    ? [
        { label: "Pemasukan", value: data.totals.income, fill: "hsl(var(--success))" },
        { label: "Pengeluaran", value: data.totals.expense, fill: "hsl(var(--destructive))" },
        { label: "Net", value: data.totals.net, fill: "hsl(var(--primary))" },
      ]
    : [];

  const download = async (fmt) => {
    try {
      const { data: blob } = await api.get(`/reports/export`, {
        params: { format: fmt, period_from: range.from, period_to: range.to },
        responseType: "blob",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `laporan_${range.from}_${range.to}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success(`Laporan ${fmt.toUpperCase()} berhasil diunduh`);
    } catch (e) {
      toast.error(`Gagal mengunduh laporan ${fmt.toUpperCase()}`);
    }
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <header>
        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Laporan</div>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">Pendapatan & Pengeluaran</h1>
        <p className="text-sm text-muted-foreground mt-1">Pendapatan bersih, rekap per unit, dan export Excel / PDF.</p>
      </header>

      <Card>
        <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
          <div className="space-y-1.5">
            <Label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Dari</Label>
            <Input type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} data-testid="report-from-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Sampai</Label>
            <Input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} data-testid="report-to-input" />
          </div>
          <Button onClick={() => download("xlsx")} variant="outline" className="gap-2" data-testid="export-xlsx-button">
            <FileSpreadsheet className="h-4 w-4" /> Export Excel
          </Button>
          <Button onClick={() => download("pdf")} className="gap-2" data-testid="export-pdf-button">
            <FileText className="h-4 w-4" /> Export PDF
          </Button>
        </CardContent>
      </Card>

      {isLoading || !data ? (
        <div className="text-sm text-muted-foreground">Memuat...</div>
      ) : (
        <>
          <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <MetricCard label="Total Pemasukan" value={formatIDR(data.totals.income)} icon={TrendingUp} tone="success" testid="report-income" />
            <MetricCard label="Total Pengeluaran" value={formatIDR(data.totals.expense)} icon={TrendingDown} tone="destructive" testid="report-expense" />
            <MetricCard label="Pendapatan Bersih" value={formatIDR(data.totals.net)} icon={Wallet} tone={data.totals.net >= 0 ? "primary" : "destructive"} highlight testid="report-net" />
          </section>

          <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="lg:col-span-2">
              <CardContent className="p-6">
                <div className="mb-3">
                  <div className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Ringkasan periode</div>
                  <div className="font-display text-xl font-medium tracking-tight">
                    {formatDate(data.period_from)} – {formatDate(data.period_to)}
                  </div>
                </div>
                <div className="h-64 -ml-3">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                      <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))"
                        tickFormatter={(v) => (v >= 1_000_000 ? `${(v/1_000_000).toFixed(0)}jt` : v >= 1000 ? `${(v/1000).toFixed(0)}rb` : v)} />
                      <Tooltip formatter={(v) => formatIDR(v)}
                        contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 12 }} />
                      <Bar dataKey="value" radius={[4,4,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="font-display text-lg font-medium tracking-tight mb-3">Pengeluaran per Kategori</div>
                {Object.keys(data.expense_by_category || {}).length === 0 ? (
                  <div className="text-sm text-muted-foreground py-4">Belum ada pengeluaran di periode ini.</div>
                ) : (
                  <div className="space-y-2 text-sm">
                    {Object.entries(data.expense_by_category).map(([k, v]) => (
                      <div key={k} className="flex items-center justify-between py-1 border-b border-border/50 last:border-0">
                        <span>{CAT_LABEL[k] || k}</span>
                        <span className="tabular-nums font-medium">{formatIDR(v)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </section>

          <section>
            <div className="mb-3">
              <h2 className="font-display text-xl font-medium tracking-tight">Tren 6 Bulan Terakhir</h2>
              <p className="text-sm text-muted-foreground">Pemasukan vs pengeluaran bulan demi bulan (tidak mengikuti filter tanggal di atas).</p>
            </div>
            <Card data-testid="report-trend-card">
              <CardContent className="p-6">
                {trendData.length === 0 ? (
                  <div className="text-sm text-muted-foreground py-6">Memuat data tren...</div>
                ) : (
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={trendData} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                        <XAxis dataKey="label" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                        <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))"
                          tickFormatter={(v) => (Math.abs(v) >= 1_000_000 ? `${(v/1_000_000).toFixed(1)}jt` : Math.abs(v) >= 1000 ? `${(v/1000).toFixed(0)}rb` : v)} />
                        <Tooltip formatter={(v) => formatIDR(v)}
                          contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 12 }} />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        <Line type="monotone" dataKey="Pemasukan" stroke="hsl(var(--success))" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                        <Line type="monotone" dataKey="Pengeluaran" stroke="hsl(var(--destructive))" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                        <Line type="monotone" dataKey="Net" stroke="hsl(var(--primary))" strokeWidth={2} strokeDasharray="4 4" dot={{ r: 2 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </CardContent>
            </Card>
          </section>

          <section>
            <h2 className="font-display text-xl font-medium tracking-tight mb-3">Rekap per Unit</h2>
            {data.per_unit.length === 0 ? (
              <Card><CardContent className="p-6 text-sm text-muted-foreground">Belum ada aktivitas per unit dalam periode ini.</CardContent></Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {data.per_unit.map((u) => (
                  <Card key={u.unit_id} data-testid={`report-unit-${u.code}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <Badge className="rounded-md font-mono">{u.code}</Badge>
                          <div className="font-medium truncate mt-2">{u.name || "—"}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Net</div>
                          <div className={`font-display text-lg font-medium tabular-nums ${u.net >= 0 ? "" : "text-destructive"}`}>
                            {formatIDR(u.net)}
                          </div>
                        </div>
                      </div>
                      <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Masuk</div>
                          <div className="tabular-nums text-success">{formatIDR(u.income)}</div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Keluar</div>
                          <div className="tabular-nums text-destructive">{formatIDR(u.expense)}</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function MetricCard({ label, value, icon: Icon, tone, highlight, testid }) {
  const toneCls =
    tone === "success" ? "bg-success/10 text-success" :
    tone === "destructive" ? "bg-destructive/10 text-destructive" :
    "bg-primary/10 text-primary";
  return (
    <Card className={highlight ? "border-primary/40" : ""} data-testid={testid}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{label}</div>
            <div className="font-display text-3xl font-medium tracking-tighter mt-2 tabular-nums">{value}</div>
          </div>
          <div className={`h-10 w-10 rounded-md grid place-items-center shrink-0 ${toneCls}`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
